import { EventType } from "@/analytics/analytics-events";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { addIElementsToStore } from "@/store/ielements-store-utilities";
import { selectAreaFor } from "@/store/selections-selectors";
import { store } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { selectIElement } from "@faro-lotv/app-component-toolbox";
import {
  Dropdown,
  FaroMenuItem,
  MoveElementIcon,
  NoTranslate,
  Option,
  TranslateVar,
  TruncatedFaroText,
  useDialog,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  createIElement,
  IElementAreaSection,
  IElementGroup,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementWithTypeAndHint,
  WithHint,
} from "@faro-lotv/ielement-types";
import {
  selectAllIElementsOfType,
  selectChildDepthFirst,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  createMutationMoveRoomToArea,
  MutationMoveRoomToArea,
  ProjectApi,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useRef, useState, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

type MoveWaypointContentProps = {
  /** The ID of the waypoint element */
  elementId: GUID;
  /** Whether to use dark mode styles */
  dark?: boolean;
  /** Optional callback to close the menu */
  closeContextMenu?(): void;
};

/**
 * @param elementId The ID of the waypoint element
 * @returns An action to move a waypoint to a different area
 */
export function MoveWaypointToDifferentArea({
  elementId,
  dark,
  closeContextMenu,
}: MoveWaypointContentProps): JSX.Element | null {
  const { openToast } = useToast();
  const dispatch = useAppDispatch();

  const { createDialog, setConfirmButtonDisabled } = useDialog();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithDialog } = useErrorHandlers();

  const iElement = useAppSelector(selectIElement(elementId));
  const currentArea = useAppSelector(selectAreaFor(iElement));
  const allAreas = useAppSelector(
    selectAllIElementsOfType(isIElementAreaSection),
    isEqual,
  );

  assert(iElement, "The waypoint element must exist");

  const targetAreaRef = useRef<IElementAreaSection>(undefined);

  const moveWaypoint = useCallback(async () => {
    if (!targetAreaRef.current) return false;

    Analytics.track(EventType.moveWaypointToArea);

    try {
      const roomsGroupToCreate = await getRoomsGroupToCreateIfNeeded(
        targetAreaRef.current,
        projectApiClient,
      );

      await projectApiClient.applyMutations([
        createMutationMoveRoomToArea(
          iElement.id,
          targetAreaRef.current.id,
          roomsGroupToCreate,
        ),
      ]);

      // Fetch the changed sub-tree and update the local copy of the project
      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: [
              currentArea?.id ?? iElement.parentId ?? iElement.id,
              targetAreaRef.current.id,
            ],
          },
        }),
      );

      openToast({
        title: (
          <>
            Waypoint moved to{" "}
            <TranslateVar name="targetArea">
              {targetAreaRef.current.name}
            </TranslateVar>
          </>
        ),
      });

      return true;
    } catch (error) {
      handleErrorWithDialog({
        title: "Unable to move Element",
        error,
      });
      return false;
    }
  }, [
    currentArea?.id,
    dispatch,
    handleErrorWithDialog,
    iElement,
    openToast,
    projectApiClient,
  ]);

  const onClick = useCallback(() => {
    closeContextMenu?.();

    createDialog({
      title: "Move waypoint to a different area",
      confirmText: "Move",
      isConfirmDisabled: true,
      content: (
        <MoveWaypointForm
          onTargetChanged={(value) => {
            targetAreaRef.current = value;
            setConfirmButtonDisabled(!value || value.id === currentArea?.id);
          }}
          currentArea={currentArea}
          allAreas={allAreas}
        />
      ),
      allowToCloseWithoutCancelling: true,
      size: "m",
      showXButton: true,
      onConfirm: moveWaypoint,
    });
  }, [
    closeContextMenu,
    createDialog,
    currentArea,
    allAreas,
    moveWaypoint,
    setConfirmButtonDisabled,
  ]);

  return (
    <FaroMenuItem
      label="Move to different area"
      aria-label="Move to different area"
      Icon={MoveElementIcon}
      onClick={onClick}
      dark={dark}
    />
  );
}

export const MOVE_WAYPOINT_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.moveWaypointToOtherArea,
  CustomComponent: MoveWaypointToDifferentArea,
};

/**
 * @returns A "Rooms" group to create, if it does not exist yet in an area.
 * Will fetch the needed elements for the check, if they are not already in the store.
 * @param targetArea The area to check for the "Rooms" group.
 * @param projectApiClient The API client to fetch the elements with.
 */
export async function getRoomsGroupToCreateIfNeeded(
  targetArea: IElementAreaSection,
  projectApiClient: ProjectApi,
): Promise<MutationMoveRoomToArea["roomsGroup"] | undefined> {
  // Make sure that the "Rooms" group of an area is fetched, which is the direct child of an area.
  // Note: It may also not exist in areas without rooms, in which case we need to create it with the mutation.
  const areChildrenFetched = (targetArea.childrenIds ?? []).every(
    (childId) => !!selectIElement(childId)(store.getState()),
  );
  if (!areChildrenFetched) {
    const children = await projectApiClient.getAllIElements({
      parentIds: [targetArea.id],
    });
    addIElementsToStore(children, store.dispatch);
  }
  const existingRoomsGroup = selectChildDepthFirst(targetArea, (el) =>
    isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.rooms),
  )(store.getState());

  if (existingRoomsGroup) return;

  const rootId = selectRootId(store.getState());
  const createdBy = selectCurrentUser(store.getState())?.id;

  assert(
    rootId && createdBy,
    "Missing expected information to create a rooms group",
  );

  return createIElement<WithHint<IElementGroup, IElementTypeHint.rooms>>({
    type: IElementType.group,
    typeHint: IElementTypeHint.rooms,
    name: "Rooms",
    parentId: targetArea.id,
    xOr: false,
    rootId,
    createdBy,
  });
}

type MoveWaypointFormProps = {
  /** Callback executed when the target changed */
  onTargetChanged(iElement: IElementAreaSection | undefined): void;

  /** The area of the iElement */
  currentArea?: IElementAreaSection;

  /** The list of all available areas the element can be moved into */
  allAreas: IElementAreaSection[];
};

function MoveWaypointForm({
  onTargetChanged,
  currentArea,
  allAreas,
}: MoveWaypointFormProps): JSX.Element {
  const [selectedArea, setSelectedArea] = useState<
    IElementAreaSection | undefined
  >(currentArea);

  const options: Option[] = useMemo(
    () =>
      allAreas.map((area) => ({
        label: <NoTranslate>{area.name}</NoTranslate>,
        value: area.id,
        key: area.id,
      })),
    [allAreas],
  );

  return (
    <>
      {currentArea && (
        <TruncatedFaroText variant="bodyM" sx={{ mb: 3 }}>
          Current area:{" "}
          <b>
            <TranslateVar name="areaName">{currentArea.name}</TranslateVar>
          </b>
        </TruncatedFaroText>
      )}

      <Dropdown
        label="Move to area"
        fullWidth
        value={selectedArea?.id}
        options={options}
        onChange={(ev) => {
          const area = allAreas.find((area) => area.id === ev.target.value);
          setSelectedArea(area);
          onTargetChanged(area);
        }}
      />
    </>
  );
}
